/**
 * Tiny DOM helpers shared by every studio panel. Keeps the panels
 * themselves focused on their own behavior rather than repeating
 * `createElement` ceremony for the same pixel-box structure 10 times.
 */

/**
 * Build the standard pixel-box wrapper:
 *
 *   <div class="studio-pixel-box">
 *     <div class="studio-pixel-box-fill">{children}</div>
 *   </div>
 *
 * Returns both the outer wrapper (to mount in the host) and the
 * inner fill (the actual content surface for the panel).
 */
export function buildPixelBox(options: { slim?: boolean } = {}): {
  outer: HTMLDivElement;
  fill: HTMLDivElement;
} {
  const outer = document.createElement("div");
  outer.className = "studio-pixel-box";
  const fill = document.createElement("div");
  fill.className = options.slim
    ? "studio-pixel-box-fill is-slim"
    : "studio-pixel-box-fill";
  outer.appendChild(fill);
  return { outer, fill };
}

/** Build a simple <button> with the studio button styling. */
export function buildStudioButton(
  label: string,
  options: {
    variant?: "primary" | "secondary" | "danger";
    onClick?: () => void;
    type?: "button" | "submit";
  } = {},
): HTMLButtonElement {
  const button = document.createElement("button");
  button.type = options.type ?? "button";
  button.textContent = label;
  const classes = ["studio-button"];
  if (options.variant === "primary") classes.push("is-primary");
  if (options.variant === "danger") classes.push("is-danger");
  button.className = classes.join(" ");
  if (options.onClick) {
    button.addEventListener("click", options.onClick);
  }
  return button;
}

/** Build a labeled field with an `<input>` or `<textarea>`. */
export function buildLabeledField(
  labelText: string,
  control: HTMLElement,
): HTMLDivElement {
  const wrapper = document.createElement("div");
  wrapper.className = "studio-field";
  const label = document.createElement("label");
  label.className = "studio-field-label";
  label.textContent = labelText;
  wrapper.appendChild(label);
  wrapper.appendChild(control);
  return wrapper;
}

/** Build an empty-state placeholder card. */
export function buildEmptyState(text: string): HTMLDivElement {
  const div = document.createElement("div");
  div.className = "studio-empty-state";
  div.textContent = text;
  return div;
}

/** Build a section heading inside a panel. */
export function buildSectionLabel(text: string): HTMLDivElement {
  const div = document.createElement("div");
  div.className = "studio-section-label";
  div.textContent = text;
  return div;
}

/** Build a paragraph with the studio helper styling. */
export function buildHelperParagraph(text: string): HTMLParagraphElement {
  const p = document.createElement("p");
  p.className = "studio-helper";
  p.textContent = text;
  return p;
}
